/**
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * 📱 TABLE DEVICES SERVICE
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 * 
 * Service layer für Table Device Management:
 * - Tisch-Registrierung (public, anonymous user creation)
 * - Tisch-Verwaltung (Club Admins/Organizers)
 * - Spiel-Zuweisung
 * - Heartbeat/Monitoring
 * - Status-Updates
 * ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */

import { supabase } from '../lib/supabase';
import type {
  TableDevice,
  TableDeviceWithDetails,
  CreateTableDeviceParams,
  UpdateTableDeviceParams,
  TableDeviceHeartbeat,
  TableDeviceStatus,
} from '../types';

// ============================================================================
// REGISTRATION (PUBLIC - Anonymous User Creation)
// ============================================================================

/**
 * Registriert einen neuen Tisch
 * - Erstellt einen anonymous Supabase User (via signInAnonymously)
 * - Erstellt table_device Eintrag
 * - Returniert device_uuid für lokalen Storage
 * 
 * WICHTIG: Verwendet signInAnonymously() statt signUp()
 * - Keine Email-Rate-Limits!
 * - Perfekt für Devices/Tablets
 * - Jeder Tisch bekommt seinen eigenen anonymous user
 */
export async function registerTableDevice(
  params: CreateTableDeviceParams
): Promise<{ device: TableDevice; deviceUuid: string }> {
  // 1. Create anonymous Supabase user (NO rate limits!)
  const { data: authData, error: authError } = await supabase.auth.signInAnonymously();

  if (authError || !authData.user) {
    throw new Error(`Failed to create anonymous user: ${authError?.message}`);
  }

  const deviceUuid = authData.user.id;

  // 2. Create table_device entry
  const { data, error } = await supabase
    .from('table_devices')
    .insert({
      device_uuid: deviceUuid,
      table_name: params.table_name,
      club_id: params.club_id,
      location: params.location,
      device_info: params.device_info,
      status: 'pending',
    })
    .select()
    .single();

  if (error) throw error;

  return { device: data, deviceUuid };
}

// ============================================================================
// READ OPERATIONS
// ============================================================================

/**
 * Lädt einen spezifischen Tisch (mit Details)
 */
export async function getTableDevice(
  deviceId: number
): Promise<TableDeviceWithDetails | null> {
  const { data, error } = await supabase
    .from('table_devices')
    .select(
      `
      *,
      club:clubs(id, name),
      current_game:games!table_devices_current_game_id_fkey(
        id,
        status,
        player_a:players!games_playerA_fkey(id, preName, name, profile_picture_url),
        player_b:players!games_playerB_fkey(id, preName, name, profile_picture_url)
      ),
      approved_by_player:players!table_devices_approved_by_fkey(id, preName, name),
      game_assigned_by_player:players!table_devices_game_assigned_by_fkey(id, preName, name)
    `
    )
    .eq('id', deviceId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  return data;
}

/**
 * Lädt einen Tisch anhand der device_uuid (für den Tisch selbst)
 */
export async function getTableDeviceByUuid(
  deviceUuid: string
): Promise<TableDevice | null> {
  const { data, error } = await supabase
    .from('table_devices')
    .select('*')
    .eq('device_uuid', deviceUuid)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data;
}

/**
 * Lädt alle Tische eines Clubs (für Admins/Organizers)
 */
export async function getClubTableDevices(
  clubId: number
): Promise<TableDeviceWithDetails[]> {
  const { data, error } = await supabase
    .from('table_devices')
    .select(
      `
      *,
      club:clubs(id, name),
      current_game:games!table_devices_current_game_id_fkey(
        id,
        status,
        player_a:players!games_playerA_fkey(id, preName, name, profile_picture_url),
        player_b:players!games_playerB_fkey(id, preName, name, profile_picture_url)
      ),
      approved_by_player:players!table_devices_approved_by_fkey(id, preName, name),
      game_assigned_by_player:players!table_devices_game_assigned_by_fkey(id, preName, name)
    `
    )
    .eq('club_id', clubId)
    .order('table_name');

  if (error) throw error;
  return data || [];
}

/**
 * Lädt alle Tische mit einem spezifischen Status
 */
export async function getTableDevicesByStatus(
  clubId: number,
  status: TableDeviceStatus
): Promise<TableDeviceWithDetails[]> {
  const { data, error } = await supabase
    .from('table_devices')
    .select(
      `
      *,
      club:clubs(id, name),
      current_game:games!table_devices_current_game_id_fkey(
        id,
        status,
        player_a:players!games_playerA_fkey(id, preName, name, profile_picture_url),
        player_b:players!games_playerB_fkey(id, preName, name, profile_picture_url)
      ),
      approved_by_player:players!table_devices_approved_by_fkey(id, preName, name),
      game_assigned_by_player:players!table_devices_game_assigned_by_fkey(id, preName, name)
    `
    )
    .eq('club_id', clubId)
    .eq('status', status)
    .order('table_name');

  if (error) throw error;
  return data || [];
}

// ============================================================================
// UPDATE OPERATIONS
// ============================================================================

/**
 * Aktualisiert einen Tisch (für Admins)
 */
export async function updateTableDevice(
  deviceId: number,
  updates: UpdateTableDeviceParams
): Promise<TableDevice> {
  const { data, error } = await supabase
    .from('table_devices')
    .update(updates)
    .eq('id', deviceId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Approved einen Tisch (Admin bestätigt Registrierung)
 */
export async function approveTableDevice(
  deviceId: number,
  approverId: number
): Promise<TableDevice> {
  const { data, error } = await supabase
    .from('table_devices')
    .update({
      status: 'approved',
      approved_at: new Date().toISOString(),
      approved_by: approverId,
    })
    .eq('id', deviceId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Deaktiviert einen Tisch
 */
export async function disableTableDevice(deviceId: number): Promise<TableDevice> {
  const { data, error } = await supabase
    .from('table_devices')
    .update({
      status: 'disabled',
      current_game_id: null,
      game_assigned_at: null,
      game_assigned_by: null,
    })
    .eq('id', deviceId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Weist ein Spiel zu einem Tisch zu
 */
export async function assignGameToTable(
  deviceId: number,
  gameId: number,
  assignedBy: number
): Promise<TableDevice> {
  const { data, error } = await supabase
    .from('table_devices')
    .update({
      current_game_id: gameId,
      game_assigned_at: new Date().toISOString(),
      game_assigned_by: assignedBy,
    })
    .eq('id', deviceId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Entfernt die Spiel-Zuweisung von einem Tisch
 */
export async function unassignGameFromTable(
  deviceId: number
): Promise<TableDevice> {
  const { data, error } = await supabase
    .from('table_devices')
    .update({
      current_game_id: null,
      game_assigned_at: null,
      game_assigned_by: null,
    })
    .eq('id', deviceId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// ============================================================================
// HEARTBEAT / MONITORING
// ============================================================================

/**
 * Sendet ein Heartbeat vom Tisch (last_seen Update)
 * Der Tisch muss als dieser User eingeloggt sein!
 */
export async function sendHeartbeat(
  deviceUuid: string,
  includeActivity: boolean = false
): Promise<void> {
  const updates: TableDeviceHeartbeat = {
    last_seen: new Date().toISOString(),
  };

  if (includeActivity) {
    updates.last_activity = new Date().toISOString();
  }

  const { error } = await supabase
    .from('table_devices')
    .update(updates)
    .eq('device_uuid', deviceUuid);

  if (error) throw error;
}

/**
 * Prüft, ob ein Tisch online ist (last_seen < 2 Minuten)
 */
export function isTableOnline(lastSeen?: string): boolean {
  if (!lastSeen) return false;

  const lastSeenDate = new Date(lastSeen);
  const now = new Date();
  const diffMinutes = (now.getTime() - lastSeenDate.getTime()) / 1000 / 60;

  return diffMinutes < 2; // Online wenn letzter Ping < 2 Minuten
}

/**
 * Prüft, ob ein Tisch aktiv ist (last_activity < 10 Minuten)
 */
export function isTableActive(lastActivity?: string): boolean {
  if (!lastActivity) return false;

  const lastActivityDate = new Date(lastActivity);
  const now = new Date();
  const diffMinutes = (now.getTime() - lastActivityDate.getTime()) / 1000 / 60;

  return diffMinutes < 10; // Aktiv wenn letzte Aktivität < 10 Minuten
}

// ============================================================================
// DELETE OPERATION
// ============================================================================

/**
 * Löscht einen Tisch
 * - Nur für Club Admins (via RLS Policy)
 * - Löscht auch den zugehörigen anonymous Supabase User
 */
export async function deleteTableDevice(deviceId: number): Promise<void> {
  // 1. Get device_uuid before deletion
  const device = await getTableDevice(deviceId);
  if (!device) throw new Error('Table device not found');

  // 2. Delete table_device entry (RLS Policy checks permission)
  const { error: deleteError } = await supabase
    .from('table_devices')
    .delete()
    .eq('id', deviceId);

  if (deleteError) throw deleteError;

  // 3. Delete anonymous user (via Admin API - needs service role key)
  // NOTE: This requires a server-side function or Edge Function!
  // For now, we'll leave the user in auth.users (will be CASCADE deleted by FK)
  // TODO: Implement Edge Function for complete cleanup
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Extrahiert Device Info aus dem Browser
 */
export function getDeviceInfo(): CreateTableDeviceParams['device_info'] {
  return {
    browser: navigator.userAgent.split('/')[0],
    os: navigator.platform,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    user_agent: navigator.userAgent,
  };
}

/**
 * Generiert einen Table Name Vorschlag basierend auf existierenden Tischen
 */
export async function suggestTableName(clubId?: number): Promise<string> {
  if (!clubId) return 'Tisch 1';

  const { data, error } = await supabase
    .from('table_devices')
    .select('table_name')
    .eq('club_id', clubId)
    .like('table_name', 'Tisch %');

  if (error || !data) return 'Tisch 1';

  // Extract numbers from existing table names
  const numbers = data
    .map((d) => {
      const match = d.table_name.match(/Tisch (\d+)/);
      return match ? parseInt(match[1], 10) : 0;
    })
    .filter((n) => n > 0);

  if (numbers.length === 0) return 'Tisch 1';

  const maxNumber = Math.max(...numbers);
  return `Tisch ${maxNumber + 1}`;
}

